import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorBrandBlue1, ColorNeutralWhite, Spacing8, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import type { ProductReviewListModel } from 'autogen/swagger/all';
import { PercentRecommended } from 'autogen/translation-keys/trans-website-productreview-header';
import { BasedOnXReviews, RatingSpreadHeadline } from 'autogen/translation-keys/trans-website-productreview-list';
import * as Typography from 'components/__deprecated/typography';
import RatingStars from 'components/generic/ratingStars';
import { useQuickView } from 'components/quickView';
import format from 'helpers/translations';
import { rem } from 'modules/helpers/style';
import { breakpoint, sizes } from 'variables';
import { CardContainer, Container } from '../shared/styles';
import RatingSpread from './ratingSpread';
import WriteReviewButton from './WriteReviewButton';

const Wrapper = styled(CardContainer)`
    display: flex;
    flex-direction: column;
    padding: ${Spacing16};
    background-color: ${ColorNeutralWhite};

    ${breakpoint.up(sizes.lg)} {
        padding: ${Spacing24};
    }
`;

const AverageRatingContainer = styled.div`
    display: flex;
    align-items: center;
`;

const AverageRating = styled.div`
    font-weight: bold;
    flex: 0 0 auto;
    line-height: 1.1;
    margin-right: 10px;
    color: ${ColorBrandBlue1};
    font-size: ${rem(46)};

    ${breakpoint.up(sizes.md)} {
        font-size: ${rem(60)};
    }
`;

const BasedOnContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const BasedOn = styled.div`
    font-weight: normal;
    opacity: 0.6;
`;

const Stars = styled(RatingStars)``;

const RatingSpreadtext = styled.div`
    margin-bottom: ${Spacing8};
`;

const WriteReviewButtonStyled = styled(WriteReviewButton)``;

const StyledCaption = styled(Typography.Caption)`
    cursor: pointer;

    margin-top: ${Spacing8};
    text-align: center;

    ${breakpoint.up(sizes.md)} {
        text-align: left;
    }
    b {
        text-decoration: underline;
    }
`;

const RatingSpreadContainer = styled(Container)`
    margin-bottom: ${Spacing16};
`;

const RatingSpreadComponent = styled(RatingSpread)``;

interface IRatingOverviewProps {
    model: ProductReviewListModel;
}

export const RatingOverview = (props: IRatingOverviewProps): JSX.Element => {
    const { toggle: openQuickview } = useQuickView('ReviewComplianceQuickview');
    const { model, ...rest } = props;
    const {
        reviewStatistics: { averageRating, reviewCount, recommendedPercent },
        complianceHeader,
        showRecommendedPercentage
    } = model;
    const { t } = useTranslation();
    const basedOnText = format(t(BasedOnXReviews), reviewCount);
    const calculatedRecommendedPercentage = Math.round(recommendedPercent * 100);

    return (
        <Wrapper {...rest}>
            <Container>
                <AverageRatingContainer>
                    <AverageRating>{averageRating}</AverageRating>
                    <BasedOnContainer>
                        <BasedOn>{basedOnText}</BasedOn>
                        <Stars rating={averageRating} size="L" />
                    </BasedOnContainer>
                </AverageRatingContainer>
                {showRecommendedPercentage && format(t(PercentRecommended), `${calculatedRecommendedPercentage}%`)}
            </Container>
            <RatingSpreadContainer>
                <RatingSpreadtext>{t(RatingSpreadHeadline)}</RatingSpreadtext>
                <RatingSpreadComponent model={model.reviewStatistics} />
            </RatingSpreadContainer>
            <WriteReviewButtonStyled model={model} />
            <StyledCaption size="md" onClick={() => openQuickview(true)}>
                {format(complianceHeader)}
            </StyledCaption>
        </Wrapper>
    );
};

export default RatingOverview;
